import { graphqlPost } from "api/graphqlClient";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { buildRecordListAdvancedSearchPath } from "../advancedSearchLink";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { RecordListWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";

const POLL_INTERVAL_MS = 10000;

/**
 * `stat_tile` widget — shows a count of records matching an optional filter.
 *
 * Queries `<type>_list(filter, limit:0) { <type>_count }` so no records are
 * transferred — just the total count.
 */
export const StatTileWidget = ({ config }: WidgetComponentProps<RecordListWidgetConfig>) => {
    const history = useHistory();
    const [count, setCount] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const isMounted = useRef(true);
    // The last count committed to state — used to skip a no-op re-render when a
    // `live` re-poll returns the identical count (see fetchCount).
    const prevCountRef = useRef<number | undefined>(undefined);

    const recordType = config?.record_type;
    const apiType = typeof recordType === "string" ? recordType.toLowerCase() : "";
    const live = config?.live ?? false;
    const filter =
        config?.filter == null
            ? null
            : typeof config.filter === "string"
            ? config.filter
            : JSON.stringify(config.filter);

    const fetchCount = useCallback(async (): Promise<boolean> => {
        if (!apiType) {
            if (isMounted.current) {
                setError("Widget config is missing a record_type.");
            }
            return false;
        }
        const listField = `${apiType}_list`;
        const countField = `${apiType}_count`;
        const query = `query DashboardStatTile($filter: String) {
            ${listField}(filter: $filter, limit: 0) {
                ${countField}
            }
        }`;
        try {
            const response = await graphqlPost({ query, variables: { filter } });
            const data = response?.data?.[listField];
            const next: number = data?.[countField] ?? 0;
            // Skip the re-render (and the lastUpdatedAt bump) when a `live`
            // re-poll returns the identical count.
            const changed = prevCountRef.current !== next;
            if (isMounted.current) {
                if (changed) {
                    prevCountRef.current = next;
                    setCount(next);
                }
                setError(null);
            }
            return changed;
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load count."));
            }
            return false;
        }
    }, [apiType, filter]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchCount,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const openAdvancedSearch = useCallback(() => {
        if (!recordType) {
            return;
        }
        history.push(buildRecordListAdvancedSearchPath(recordType, config?.filter));
    }, [config?.filter, history, recordType]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openAdvancedSearch();
            }
        },
        [openAdvancedSearch]
    );

    const isLink = Boolean(recordType);

    let content;
    // Keep the last count on a failed re-fetch; surface the error inline on the
    // live bar instead of blanking.
    if (error && count === null) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else {
        content = (
            <div
                className={`DashboardStatTile${isLink ? " DashboardStatTile--link" : ""}`}
                {...(isLink
                    ? {
                          role: "link",
                          tabIndex: 0,
                          onClick: openAdvancedSearch,
                          onKeyDown,
                          title: "Open matching records in Advanced search",
                      }
                    : {})}
            >
                <span className="DashboardStatTile__count">
                    {count === null ? "…" : count}
                </span>
                {recordType && (
                    <span className="DashboardStatTile__label">
                        {recordType.replace(/_/g, " ").toLowerCase()}
                    </span>
                )}
            </div>
        );
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
